import {
  collection,
  doc,
  getDoc,
  getDocs,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore'
import type { Household, HouseholdMember } from '@domain/household'
import type { HouseholdRepository } from '@domain/ports/household-repository'
import { toIso } from './converters'

/**
 * Leitura da casa e dos membros. **Só leitura** — quem escreve em
 * `households`, `members` e `users` é a callable `bootstrapHousehold`, com
 * Admin SDK, e as regras negam escrita do cliente (`allow write: if false`).
 * Ver Docs/05-Plano/Fase 1 - Plano de execução.md.
 *
 * A conversão de `Timestamp` para string ISO mora na fronteira, porque o
 * domínio não importa Firebase — e desde a Task 8 da Fase 2 ela mora em
 * `converters.ts`, um lugar só para as duas famílias de repositório. Este
 * arquivo tinha o `toIso` próprio enquanto era o único leitor do Firestore no
 * cliente; com os quatro repositórios do núcleo financeiro ao lado, duas cópias
 * seriam duas fronteiras, e a nota promete uma.
 */

/** Id fixo: há exatamente uma casa. O mesmo literal existe em
 * `functions/src/household/create-household.ts` — pacotes separados. */
export const HOUSEHOLD_ID = 'casa'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

export function toHousehold(id: string, data: DocumentData): Household {
  const createdAt = toIso(data['createdAt'])
  if (createdAt === null) {
    // A casa é a raiz da posse de todo dado do sistema. Um documento de casa
    // sem `createdAt` não é um caso a contornar com valor default: é sinal de
    // que algo que não é o bootstrap escreveu ali.
    throw new Error(`household-repository: households/${id} sem createdAt válido.`)
  }

  const settings = isRecord(data['settings']) ? data['settings'] : {}

  return {
    id,
    // O nome canônico é o que o bootstrap escreveu; o default só evita casa
    // sem nome na UI se o campo sumir.
    name: typeof data['name'] === 'string' ? data['name'] : 'Casa',
    settings: {
      // `timezone` e `currency` admitem exatamente um valor cada no tipo do
      // domínio. Ler do documento só criaria uma forma de estarem errados.
      timezone: 'America/Sao_Paulo',
      currency: 'BRL',
      weekStartsOn: settings['weekStartsOn'] === 1 ? 1 : 0,
    },
    createdAt,
  }
}

export function toMember(id: string, data: DocumentData): HouseholdMember {
  return {
    // O id do documento é o uid, e é ele que o `isMember()` das regras
    // consulta. Se o campo `userId` divergir do id, o id é a verdade.
    userId: id,
    email: typeof data['email'] === 'string' ? data['email'] : '',
    displayName: typeof data['displayName'] === 'string' ? data['displayName'] : null,
    photoURL: typeof data['photoURL'] === 'string' ? data['photoURL'] : null,
    joinedAt: toIso(data['joinedAt']),
  }
}

/**
 * `true` quando o erro é a **negação das regras**, que é a única forma pela
 * qual "o bootstrap ainda não rodou para este uid" chega ao cliente.
 *
 * Não existe caminho em que as leituras abaixo resolvam com "casa ausente":
 * sem `members/{uid}` o `isMember()` de `firestore.rules` é falso e a leitura
 * é negada antes de chegar a qualquer documento. Isso não é leitura de regra,
 * é teste executado — `tests/rules/firestore.rules.test.ts`, casos
 * "não-membro não lê o documento da household" e "não-membro não lê perfil de
 * membro", cada um com o positivo do mesmo `allow` ao lado. E com
 * `members/{uid}` existindo, a casa existe: as duas coisas nascem na mesma
 * transação do bootstrap.
 *
 * Distinguir dessa forma importa porque a reação é diferente: negação quer
 * dizer "chame o bootstrap"; `unavailable` quer dizer "tente de novo".
 */
export function isNotMemberYet(error: unknown): boolean {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    error.code === 'permission-denied'
  )
}

/**
 * A casa, para quem é membro dela.
 *
 * **Rejeita** — não devolve `null` — quando o bootstrap ainda não rodou para
 * este uid; o erro é o `permission-denied` das regras, e `isNotMemberYet()`
 * acima é como ramificar nele.
 */
export async function getHousehold(db: Firestore): Promise<Household> {
  const snap = await getDoc(doc(db, 'households', HOUSEHOLD_ID))
  if (!snap.exists()) {
    // Leitura permitida (logo, `members/{uid}` existe) e casa ausente é um
    // estado que a transação do bootstrap não produz. Como o `createdAt`
    // inválido em `toHousehold`, isso é sinal de que algo que não é o
    // bootstrap mexeu ali — e falhar alto é melhor que devolver `null` e
    // deixar a UI inventar uma explicação.
    throw new Error(
      `household-repository: households/${HOUSEHOLD_ID} não existe, mas a leitura foi permitida.`,
    )
  }
  return toHousehold(snap.id, snap.data())
}

/** Os membros que **já entraram**. Quem está na allowlist e ainda não fez o
 * primeiro login não tem documento — ver Docs/02-Dominio/Household.md. Mesma
 * rejeição de `getHousehold` quando o bootstrap ainda não rodou. */
export async function getMembers(db: Firestore): Promise<HouseholdMember[]> {
  const snap = await getDocs(collection(db, 'households', HOUSEHOLD_ID, 'members'))
  return snap.docs.map((member) => toMember(member.id, member.data()))
}

/**
 * A porta `HouseholdRepository` com o `db` já preso, para a composição em
 * `src/main.tsx` — o único arquivo do repositório que pode importar `adapters/`.
 * A UI recebe a porta por injeção e nunca vê o Firestore.
 */
export function createFirebaseHouseholdRepository(db: Firestore): HouseholdRepository {
  return {
    listMembers: () => getMembers(db),
  }
}
